import { create } from 'zustand';
import { fetchWallets, fetchKrakenBalance } from '../api/authApi';
import type { Wallet } from '../api/authApi';

/**
 * Polls the Kraken balance for the first active live wallet that has an
 * apiKeyId. Single 30s timer — both the header pill and any future consumer
 * read from here instead of fetching independently.
 *
 * Lifecycle:
 *   startPolling()  — called after login / wallet change; starts timer if needed
 *   stopPolling()   — called on logout / market teardown
 *
 * Error handling: on fetch failure the totalUsd is left as-is (or null if
 * never succeeded) and the error is recorded. The header pill treats an
 * error+null total as "no live data" and falls back to the paper pill.
 * The 30s cadence is the only retry; no exponential back-off, no storm.
 */

type State = {
  /** The active live wallet that is being polled, or null if none. */
  activeLiveWallet: Wallet | null;
  totalUsd: number | null;
  loading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
};

type Actions = {
  /** Fetch live wallets and start polling if one exists. Idempotent. */
  startPolling: () => Promise<void>;
  /** Stop the poll timer and reset live-balance state. */
  stopPolling: () => void;
  /** Force an immediate balance refresh (called internally by the timer). */
  refresh: () => Promise<void>;
};

// Module-level timer handle — lives outside the store so React renders
// don't accidentally capture stale closures over it.
let _timer: ReturnType<typeof setInterval> | null = null;

const INITIAL: State = {
  activeLiveWallet: null,
  totalUsd: null,
  loading: false,
  error: null,
  lastFetchedAt: null,
};

export const useWalletEquityStore = create<State & Actions>()((set, get) => ({
  ...INITIAL,

  async startPolling() {
    // Find the first active live wallet with an apiKeyId.
    let wallets: Wallet[];
    try {
      wallets = await fetchWallets();
    } catch {
      // Can't enumerate wallets — nothing to poll.
      return;
    }

    const liveWallet =
      wallets.find((w) => w.type === 'live' && w.isActive && w.apiKeyId) ?? null;

    if (!liveWallet) {
      // No live wallet — clear any previous state and stop.
      set({ activeLiveWallet: null, totalUsd: null, error: null, loading: false });
      return;
    }

    const prev = get().activeLiveWallet;
    // If the same wallet is already being polled, don't restart.
    if (prev?._id === liveWallet._id && _timer !== null) return;

    set({ activeLiveWallet: liveWallet, totalUsd: null, error: null });

    // Immediate first fetch.
    await get().refresh();

    // Clear any existing timer before starting a new one.
    if (_timer !== null) clearInterval(_timer);
    _timer = setInterval(() => {
      void get().refresh();
    }, 30_000);
  },

  stopPolling() {
    if (_timer !== null) {
      clearInterval(_timer);
      _timer = null;
    }
    set(INITIAL);
  },

  async refresh() {
    const { activeLiveWallet } = get();
    if (!activeLiveWallet?.apiKeyId) return;

    set({ loading: true });
    try {
      const result = await fetchKrakenBalance(activeLiveWallet.apiKeyId);
      set({
        totalUsd: result.totalUsd,
        loading: false,
        error: null,
        lastFetchedAt: Date.now(),
      });
    } catch (err) {
      set({
        loading: false,
        error: (err as Error).message,
        // Keep existing totalUsd so the pill doesn't flash; header will
        // check error state and fall back to paper pill on error+null.
      });
    }
  },
}));
